import * as React from "react";
import {FlowEdge as EdgeType, FlowNode as NodeType} from "./types";

type Props = {
    edge: EdgeType;
    source: NodeType;
    target: NodeType;
    isHighlighted?: boolean;
    isDimmed?: boolean;
    isBlocked?: boolean;
    renderPath?: boolean;
    renderLabel?: boolean;
};

const LABEL_FONT_SIZE = 12;
const LABEL_PADDING_X = 10;
const LABEL_PADDING_Y = 6;
const LABEL_CHAR_WIDTH = 7;
const BLOCKED_OPACITY = 0.4;
const RULE_EDGE_COLOR = "#2c5282";
const RULE_LABEL_TEXT = "#2d3748";
const RULE_LABEL_STROKE = "#cbd5e0";
const RULE_LABEL_BG = "#edf2f7";
const IMPLICIT_EDGE_COLOR = "#7fb3d5";
const IMPLICIT_LABEL_TEXT = "#4a5568";
const IMPLICIT_LABEL_STROKE = "#d7e2f0";
const IMPLICIT_LABEL_BG = "#f2f7fb";

const getLabelWidth = (label: string) => {
    return label.length * LABEL_CHAR_WIDTH + LABEL_PADDING_X * 2;
};

const FlowEdge = ({
    edge,
    source,
    target,
    isHighlighted,
    isDimmed,
    isBlocked,
    renderPath = true,
    renderLabel = true
}: Props) => {
    const startX = source.x + source.width;
    const startY = source.y + source.height / 2;
    const endX = target.x;
    const endY = target.y + target.height / 2;
    const midX = (startX + endX) / 2;
    const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
    const labelX = midX;
    const labelY = (startY + endY) / 2;
    const labelTextValue = edge.label || "";
    const showLabel = labelTextValue.trim().length > 0;
    const labelWidth = showLabel ? getLabelWidth(labelTextValue) : 0;
    const labelHeight = showLabel ? LABEL_FONT_SIZE + LABEL_PADDING_Y * 2 : 0;
    const isSoftDimmed = isDimmed || isBlocked;
    const baseStroke = edge.kind === "implicit" ? IMPLICIT_EDGE_COLOR : RULE_EDGE_COLOR;
    const baseLabelText = edge.kind === "implicit" ? IMPLICIT_LABEL_TEXT : RULE_LABEL_TEXT;
    const baseLabelStroke = edge.kind === "implicit" ? IMPLICIT_LABEL_STROKE : RULE_LABEL_STROKE;
    const baseLabelBg = edge.kind === "implicit" ? IMPLICIT_LABEL_BG : RULE_LABEL_BG;
    const strokeColor = isHighlighted ? "#c53030" : isSoftDimmed ? "#a0aec0" : baseStroke;
    const labelBg = isHighlighted ? "#fed7d7" : isSoftDimmed ? "#f7fafc" : baseLabelBg;
    const labelStroke = isHighlighted ? "#feb2b2" : isSoftDimmed ? "#e2e8f0" : baseLabelStroke;
    const labelText = isHighlighted ? "#742a2a" : isSoftDimmed ? "#a0aec0" : baseLabelText;
    const pathOpacity = isBlocked && !isHighlighted ? BLOCKED_OPACITY : 1;
    const labelOpacity = isBlocked && !isHighlighted ? BLOCKED_OPACITY : 1;

    return (
        <g>
            {renderPath ? (
                <path
                    d={path}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    opacity={pathOpacity}
                />
            ) : null}
            {renderLabel && showLabel ? (
                <g
                    transform={`translate(${labelX - labelWidth / 2}, ${labelY - labelHeight / 2})`}
                    opacity={labelOpacity}
                >
                    <rect
                        width={labelWidth}
                        height={labelHeight}
                        rx={6}
                        ry={6}
                        fill={labelBg}
                        stroke={labelStroke}
                        strokeWidth={1}
                    />
                    <text
                        x={labelWidth / 2}
                        y={labelHeight / 2}
                        fontSize={LABEL_FONT_SIZE}
                        fontWeight={600}
                        fill={labelText}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{userSelect: "none"}}
                    >
                        {labelTextValue}
                    </text>
                </g>
            ) : null}
        </g>
    );
};

export default FlowEdge;
